import { CategoryData } from "../config/types";

export interface BudgetPercentages {
  needsPercentage: number | null;
  wantsPercentage: number | null;
  savingsPercentage: number | null;
}

/**
 * Calculate budget percentages for Needs, Wants, and Savings categories
 * @param categories Array of category data
 * @returns Object with percentage values (null if no categories in that group)
 */
export function calculateBudgetPercentages(
  categories: CategoryData[],
): BudgetPercentages {
  if (!categories || categories.length === 0) {
    return {
      needsPercentage: null,
      wantsPercentage: null,
      savingsPercentage: null,
    };
  }

  // Calculate total budgeted amount
  const totalBudgeted = categories.reduce((sum, cat) => sum + cat.budgeted, 0);

  if (totalBudgeted === 0) {
    return {
      needsPercentage: null,
      wantsPercentage: null,
      savingsPercentage: null,
    };
  }

  // Calculate percentages for each category type
  const needsBudgeted = categories
    .filter((cat) => cat.necessary && !cat.savings)
    .reduce((sum, cat) => sum + cat.budgeted, 0);

  const wantsBudgeted = categories
    .filter((cat) => !cat.necessary && !cat.savings)
    .reduce((sum, cat) => sum + cat.budgeted, 0);

  const savingsBudgeted = categories
    .filter((cat) => cat.savings)
    .reduce((sum, cat) => sum + cat.budgeted, 0);

  return {
    needsPercentage:
      needsBudgeted > 0
        ? Math.floor((needsBudgeted / totalBudgeted) * 100)
        : null,
    wantsPercentage:
      wantsBudgeted > 0
        ? Math.floor((wantsBudgeted / totalBudgeted) * 100)
        : null,
    savingsPercentage:
      savingsBudgeted > 0
        ? Math.floor((savingsBudgeted / totalBudgeted) * 100)
        : null,
  };
}
